import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useFeatureFlags } from '@sdx/b2o-fe-tools';
import { clearPianoUser } from '@sdx/b2o-fe-tools/pianoAnalytics';

import { loginRequest, attemptSsoSilent } from '../../../msalConfig';
import { platformLogout } from './platformLogout';
import { SESSION_CLEARED_EVENT } from './UserContext';
import { useLoginPermissionCheck } from '../hooks/useLoginPermissionCheck';
import { clearSnoozedIds } from '../utils/alertBarSnooze';
import { clearPopupBannerShown } from '../../features/PopupBanner/popupBannerSession';
import { clearOsatEvaluated, clearOsatShown } from '../constants/osat';
import { OsatQueryKeys } from '../constants/osatRoutes';
import { SupportCenterQueryKeys } from '../constants/supportCenterRoutes';
import { isUnauthorizedError } from '../utils/httpErrors';
import { FeatureFlags } from '../../types';
import {
  setDatadogUser,
  clearDatadogUser,
  clearDatadogGlobalContext,
  addDatadogError,
  addDatadogLog,
  normalizeErrorForTelemetry,
} from '../services/telemetry/datadog';

type UserProfile = {
  name: string;
  email: string;
  role: string;
};

type AuthContextType = {
  userProfile: UserProfile | null;
  accessToken: string | null;
  accessTokenRef: React.RefObject<string | null>;
  checkSession: () => void;
  waitForNextToken: (timeoutMs?: number) => Promise<boolean>;
  isSsoAttempting: boolean;
  hasPermission: boolean | null;
  isAuthLoading: boolean;
  isFullyAuthenticated: boolean;
  isAuthInitializing: boolean;
  authError: string | Error | null;
  flags: Partial<FeatureFlags> | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const REFRESH_LEAD_TIME_MS = 2 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;
// Comfortable headroom for a normal acquireTokenSilent() round trip after a 401, while keeping
// the worst-case wait on an unrecoverable session bounded. Not backed by production telemetry
// yet — retune once real round-trip data is available from UAT/Datadog.
const DEFAULT_TOKEN_WAIT_MS = 3000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isSsoAttempting, setIsSsoAttempting] = useState<boolean>(true);
  const [hasLoggedOutDueToError, setHasLoggedOutDueToError] = useState<boolean>(false);
  const [isAuthInitializing, setIsAuthInitializing] = useState<boolean>(true);
  const [tokenAcquisitionError, setTokenAcquisitionError] = useState<Error | null>(null);
  const hasInitialized = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Expiry of the token we currently hold. Guards against logging a user out when a single
  // refresh attempt fails transiently (VPN blip, AAD 5xx) while their held token is still valid.
  const lastExpiresOnRef = useRef<Date | null>(null);
  // Cancellation flag: a silent-acquisition promise can resolve AFTER unmount and would
  // otherwise arm an orphaned, self-rescheduling refresh timer that nothing owns.
  const isMountedRef = useRef(true);
  // Second, independent cancellation mechanism. None of the three logout paths UNMOUNT the
  // provider — they null the token/profile and navigate — so isMountedRef stays true across a
  // logout and cannot protect against this: a silent acquisition already in flight when the user
  // is logged out would still resolve into the success handler and restore accessToken, the auth
  // cookie, the profile and the refresh timer, silently undoing the logout while the user sits on
  // /login.
  //
  // A monotonic counter rather than a boolean flag, deliberately. Each checkSession() captures the
  // generation current at ITS start and only commits its result while that generation is still the
  // live one; every logout path increments, invalidating every acquisition already in flight. A
  // boolean would have to be RESET by the next checkSession() to let a fresh sign-in through — and
  // MSAL's `accounts` array does not empty synchronously on logout (on the DOM-logout path
  // platformLogout() is an async browser navigation, so it may never empty before the page goes).
  // A visibilitychange / invalidaccesstoken firing inside that window would reset the flag and
  // un-cancel the OLD, still-pending promise from before the logout, reopening the exact race.
  // The counter never resets, so a newer call can never un-cancel an older one.
  const sessionGenerationRef = useRef(0);
  // The generation counter alone still lets a NEW checkSession() through: one that starts AFTER
  // logout captures the already-bumped generation as its own, so `gen === sessionGenerationRef`
  // holds when it settles and it is treated as legitimate. That matters because logout does not
  // clear MSAL's cache synchronously — instance.logout() (authError / hasPermission paths) and
  // especially platformLogout()'s async logoutRedirect() (the DOM 'logout' path) both leave a
  // window where accounts is still populated and acquireTokenSilent() can genuinely succeed. A
  // visibilitychange/invalidaccesstoken firing in that window would then restore the session the
  // user just ended. This flag blocks checkSession() from running at all while a logout is in
  // flight, and is cleared only once accounts.length actually reaches 0 — real evidence MSAL's
  // cache is empty, not just a promise resolving.
  const logoutInProgressRef = useRef(false);
  // Always-current mirror of effectiveAccessToken for consumers that need a fresh read inside a
  // closure created at an earlier render (e.g. an axios interceptor retrying a request) — reading
  // the state value directly there would see whatever token was live when that closure was made.
  const accessTokenRef = useRef<string | null>(null);
  const previousTokenRef = useRef<string | null>(null);
  const pendingWaitersRef = useRef<Set<(resolved: boolean) => void>>(new Set());

  const clearRefreshTimer = useCallback((): void => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const effectiveAccessToken =
    accessToken || (import.meta.env.VITE_ACCESS_TOKEN_OVERRIDE as string | undefined) || null;

  useEffect(() => {
    accessTokenRef.current = effectiveAccessToken;

    const tokenChanged =
      effectiveAccessToken != null &&
      effectiveAccessToken !== '' &&
      effectiveAccessToken !== previousTokenRef.current;
    previousTokenRef.current = effectiveAccessToken;

    if (tokenChanged) {
      pendingWaitersRef.current.forEach((resolve) => resolve(true));
      pendingWaitersRef.current.clear();
    }
  }, [effectiveAccessToken]);

  useEffect(() => {
    const pendingWaiters = pendingWaitersRef.current;

    return (): void => {
      pendingWaiters.forEach((resolve) => resolve(false));
      pendingWaiters.clear();
    };
  }, []);

  const waitForNextToken = useCallback(
    (timeoutMs: number = DEFAULT_TOKEN_WAIT_MS): Promise<boolean> =>
      new Promise((resolve) => {
        // eslint-disable-next-line prefer-const
        let timer: ReturnType<typeof setTimeout>;
        const resolveOnce = (resolved: boolean): void => {
          pendingWaitersRef.current.delete(resolveOnce);
          clearTimeout(timer);
          resolve(resolved);
        };
        timer = setTimeout(() => resolveOnce(false), timeoutMs);
        pendingWaitersRef.current.add(resolveOnce);
      }),
    [],
  );

  const baseUrl = import.meta.env.VITE_FOUNDATION_API_BASE_URL as string;

  const {
    flags,
    error: featureFlagsError,
    isLoading: isFeatureFlagsLoading,
  } = useFeatureFlags<FeatureFlags>({
    baseUrl,
    accessToken: effectiveAccessToken,
  });

  const {
    isChecking,
    hasPermission,
    error: permissionError,
  } = useLoginPermissionCheck({
    accessToken: effectiveAccessToken,
    baseUrl,
    enabled:
      flags?.ENABLE_LOGIN_PERMISSION_CHECK === true &&
      accounts.length > 0 &&
      effectiveAccessToken !== null,
  });

  const isAuthLoading = isFeatureFlagsLoading || isChecking;

  // A featureFlags 401 is a transient token-expiry error that the recovery layer heals on the
  // renewed token (@sdx/b2o-fe-tools keys its queries on the token). Suppress it from authError
  // so it does NOT drop isFullyAuthenticated (which would make AppRoutes redirect to /login) nor
  // trigger the logout effect below — but ONLY while a prior good `flags` snapshot exists
  // (`flags != null`). That guard scopes the suppression to a genuinely-authenticated session
  // recovering from expiry, and closes the fail-open hole: on first-load (no cache) or when the
  // re-keyed query resolves to a persistent 401 (keepPreviousData drops `flags` to undefined),
  // the 401 is surfaced as a real authError → the guard redirects to /login (fail-closed).
  // Genuine non-401 errors and real 403s (via permissionError / hasPermission === false) always
  // surface regardless.
  const isTransientTokenExpiry = flags != null && isUnauthorizedError(featureFlagsError);

  const authError =
    (isTransientTokenExpiry ? null : featureFlagsError) ||
    permissionError ||
    tokenAcquisitionError ||
    null;

  const isAuthRedirectEnabled = import.meta.env.VITE_FEATURE_FLAG_AUTH_REDIRECT === 'true';
  const isPermissionCheckEnabled = flags?.ENABLE_LOGIN_PERMISSION_CHECK === true;

  const isFullyAuthenticated =
    !isAuthRedirectEnabled ||
    (effectiveAccessToken != null &&
      !isAuthLoading &&
      !authError &&
      (!isPermissionCheckEnabled || hasPermission === true));

  // All three session-clear paths (authError, hasPermission === false, handleLogout)
  // run this exact sequence. Extracted so no step can be present at two sites and
  // missing at the third — the shape of both blocker-level findings in the 859323
  // review (clearOsatEvaluated/removeQueries, then setAccessToken/setUserProfile).
  // Order matters: dropping the token first makes `useOsatEligibility` `enabled: false`
  // in the same render batch as removeQueries, so the removed entry is NOT refetched on
  // the previous user's token (which would re-arm osat:evaluated); removeQueries then
  // drops the eligibility cache so the next same-tab account (native keeps the webview +
  // RQ cache alive) re-evaluates against a fresh server response rather than inheriting it.
  // It also tears down the refresh timer and the held-token expiry so no scheduled silent
  // refresh outlives the session being cleared.
  const clearSessionState = useCallback((): void => {
    setAccessToken(null);
    setUserProfile(null);
    clearRefreshTimer();
    lastExpiresOnRef.current = null;
    clearPianoUser();
    clearDatadogUser();
    clearDatadogGlobalContext();
    clearSnoozedIds();
    clearOsatEvaluated();
    clearOsatShown();
    clearPopupBannerShown();
    queryClient.removeQueries({ queryKey: [OsatQueryKeys.ELIGIBILITY] });
    for (const key of Object.values(SupportCenterQueryKeys)) {
      queryClient.removeQueries({ queryKey: [key] });
    }
    document.dispatchEvent(new Event(SESSION_CLEARED_EVENT));
  }, [queryClient, clearRefreshTimer]);

  useEffect(() => {
    if (authError && accounts.length > 0 && accessToken !== null) {
      // First statement of the logout: bumping the generation invalidates every
      // acquireTokenSilent() already in flight, so they are discarded when they settle rather
      // than restoring the session we are tearing down here.
      sessionGenerationRef.current += 1;
      logoutInProgressRef.current = true;

      const account = accounts[0];
      if (account) {
        // Local cache-clear only — onRedirectNavigate:()=>false suppresses navigation on all platforms.
        instance.logout({
          account,
          onRedirectNavigate: () => false,
        });
      }

      clearSessionState();

      setHasLoggedOutDueToError(true);

      if (location.pathname !== '/login') {
        navigate('/login', { state: { from: location }, replace: true });
      }
    }
  }, [
    authError,
    accounts.length,
    accessToken,
    navigate,
    location.pathname,
    instance,
    clearSessionState,
  ]);

  useEffect(() => {
    if (isSsoAttempting || isChecking) {
      return;
    }

    const userIsAuthenticated = accounts.length > 0 && accessToken !== null;

    if (userIsAuthenticated && hasPermission === false) {
      // See the authError effect above: discard any in-flight silent acquisition from here on.
      sessionGenerationRef.current += 1;
      logoutInProgressRef.current = true;

      const account = accounts[0];
      if (account) {
        // Local cache-clear only — onRedirectNavigate:()=>false suppresses navigation on all platforms.
        instance.logout({
          account,
          onRedirectNavigate: () => false,
        });
      }

      setHasLoggedOutDueToError(true);
      clearSessionState();

      if (location.pathname !== '/login') {
        navigate('/login', { state: { from: location }, replace: true });
      }
    }
  }, [
    hasPermission,
    isSsoAttempting,
    isChecking,
    accounts.length,
    accessToken,
    navigate,
    location.pathname,
    instance,
    clearSessionState,
  ]);

  // Captured during RENDER, before any effect for this commit can run — including the reset
  // effect immediately below, which clears logoutInProgressRef the instant accounts.length hits
  // 0. Effects for one component run in source order within a commit, so by the time the mount
  // effect further down reads logoutInProgressRef.current directly, the reset effect above it may
  // already have flipped it to false in the SAME commit — exactly the transition that empties
  // accounts. Reading the render-time snapshot instead of the live ref lets that effect's SSO gate
  // see the pre-reset value it actually needs, without racing its own cleanup.
  const wasLogoutInProgressThisRender = logoutInProgressRef.current;

  // Real evidence a logout has finished: MSAL's own accounts array emptied, not a timer or a
  // promise settling. Only then is it safe to let checkSession() run for a subsequent sign-in.
  useEffect(() => {
    if (accounts.length === 0) {
      logoutInProgressRef.current = false;
    }
  }, [accounts.length]);

  const clearInitializing = useCallback((): void => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      setIsAuthInitializing(false);
    }
  }, []);

  // A failed refresh attempt is only worth a forced logout when the token we hold is ALREADY
  // dead. While it is still valid the failure is transient (network blip, AAD 5xx) and the
  // recovery layer will heal on the next attempt — logging out here would destroy the session
  // (and any unsaved work) over something recoverable. No prior success at all (`null`) still
  // fails closed: there is no live session to protect.
  const shouldFailClosed = useCallback(
    (): boolean =>
      lastExpiresOnRef.current == null || Date.now() >= lastExpiresOnRef.current.getTime(),
    [],
  );

  const checkSession = useCallback((): void => {
    const account = accounts[0];

    if (account === undefined) {
      return;
    }

    // A logout is still tearing down (async redirect in flight, or accounts hasn't emptied yet).
    // Do not even start a new silent acquisition: unlike the generation check below — which only
    // catches calls that were ALREADY in flight before logout — a call starting now would capture
    // the post-logout generation as its own and pass that check trivially.
    if (logoutInProgressRef.current) {
      clearInitializing();
      return;
    }

    // Bind this invocation's settlement handlers to the generation live at its start. Read only —
    // never incremented or reset here, so a later call cannot revive an earlier one's promise.
    const gen = sessionGenerationRef.current;

    try {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account,
        })
        .then((response) => {
          // A logout raced this acquisition and won. Restoring the token / cookie / profile /
          // refresh timer now would resurrect the session the user just ended. Discard the
          // result, but still resolve auth-initializing so the app doesn't hang on a spinner.
          if (gen !== sessionGenerationRef.current) {
            clearInitializing();
            return;
          }

          const { accessToken: token, expiresOn } = response;
          setTokenAcquisitionError(null);
          if (token && expiresOn) {
            setAccessToken(token);
            lastExpiresOnRef.current = expiresOn;

            const msUntilExpiry = expiresOn.getTime() - Date.now();
            const refreshDelay = Math.max(
              msUntilExpiry - REFRESH_LEAD_TIME_MS,
              MIN_REFRESH_DELAY_MS,
            );

            clearRefreshTimer();
            // Skip when the provider has already unmounted: this promise can resolve after
            // cleanup ran, and the timer it would arm self-reschedules forever, unowned.
            if (isMountedRef.current) {
              refreshTimerRef.current = setTimeout(() => {
                checkSession();
              }, refreshDelay);
            }

            const target = import.meta.env.VITE_AUTH_COOKIE as string;

            Cookies.set(target, token, {
              expires: expiresOn,
              secure: true,
              sameSite: 'Strict',
            });

            const userEmail = (account.idTokenClaims?.email as string) ?? '';
            const userName = account.idTokenClaims?.name ?? '';
            const userRole = (account.idTokenClaims?.jobTitle as string) ?? '';

            const profile = {
              name: userName,
              email: userEmail,
              role: userRole,
            };

            setUserProfile(profile);

            // Identify the session in Datadog so RUM/Logs can be filtered per user.
            // Skip entirely when MSAL didn't surface either account id (rare,
            // but happens with some custom auth flows): without an id we'd be
            // creating an anonymous-but-claimed user record which is harder
            // to reason about than just leaving the session unidentified.
            const datadogUserId = account.localAccountId || account.homeAccountId;
            if (datadogUserId) {
              // Identify the Datadog session with the opaque MSAL account id
              // ONLY. email / name / jobTitle are PII and must never reach a
              // third-party telemetry sink (Sodexo observability standard);
              // Datadog is a sub-processor and this context is stamped on every
              // RUM event + Log for the session. Per-user filtering still works
              // via the stable id; the id→person mapping stays in the IdP.
              // (name/email/role remain in setUserProfile above for in-app UI.)
              setDatadogUser({ id: datadogUserId });
            }
          }
          clearInitializing();
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            // A logout raced this acquisition and won. acquireTokenRedirect() is a FULL-PAGE
            // browser navigation to AAD — firing it now would yank the user straight off /login
            // and back into the sign-in they just explicitly ended. Same generation contract as
            // the success handler above: discard, but still resolve auth-initializing so the app
            // doesn't hang on a spinner.
            if (gen !== sessionGenerationRef.current) {
              clearInitializing();
              return;
            }
            instance.acquireTokenRedirect({ ...loginRequest, account }).catch((redirectError) => {
              // genericMessage: MSAL error messages/stacks can embed account
              // ids / login hints. Datadog gets name + errorCode only.
              const { error: safeError, context } = normalizeErrorForTelemetry(redirectError, {
                genericMessage: 'Token redirect failed',
              });
              // Log the SCRUBBED error, never the raw one: forwardErrorsToLogs:true
              // (datadog.ts) forwards console.error to Datadog Logs, so a raw MSAL
              // object here would leak the same PII we just stripped.
              console.error('Error redirecting to acquire token:', safeError);
              addDatadogError(safeError, {
                source: 'AuthContext.checkSession.acquireTokenRedirect',
                ...context,
              });
            });
          } else {
            const { error: safeError, context } = normalizeErrorForTelemetry(error, {
              genericMessage: 'Silent token acquisition failed',
            });
            addDatadogError(safeError, {
              source: 'AuthContext.checkSession.acquireTokenSilent',
              ...context,
            });
            // A logout raced this acquisition and won. Telemetry above still fires (the failure
            // is real signal), but a session already being torn down must not get an error state
            // or a retry timer scheduled against it.
            if (gen !== sessionGenerationRef.current) {
              clearInitializing();
              return;
            }
            if (shouldFailClosed()) {
              setTokenAcquisitionError(safeError);
            } else {
              // Transient failure with a still-valid held token: nothing else is scheduled to
              // retry (the success path is the only place that arms this timer), so without this
              // the token would silently expire on a focused tab with no downstream 401 to
              // trigger a reactive checkSession(). Retry after the same floor delay used for a
              // normal proactive refresh.
              clearRefreshTimer();
              if (isMountedRef.current) {
                refreshTimerRef.current = setTimeout(() => {
                  checkSession();
                }, MIN_REFRESH_DELAY_MS);
              }
            }
          }
          clearInitializing();
        });
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // See the matching guard in the async .catch() branch above: never fire a full-page
        // redirect to AAD on behalf of a session that has already been torn down.
        if (gen !== sessionGenerationRef.current) {
          clearInitializing();
          return;
        }
        instance.acquireTokenRedirect({ ...loginRequest, account }).catch((redirectError) => {
          const { error: safeError, context } = normalizeErrorForTelemetry(redirectError, {
            genericMessage: 'Token redirect failed',
          });
          console.error('Error redirecting to acquire token:', safeError);
          addDatadogError(safeError, {
            source: 'AuthContext.checkSession.acquireTokenRedirect.sync',
            ...context,
          });
        });
      } else {
        const { error: safeError, context } = normalizeErrorForTelemetry(error, {
          genericMessage: 'Session check failed',
        });
        addDatadogError(safeError, { source: 'AuthContext.checkSession.sync', ...context });
        // See the matching guard in the async .catch() branch above.
        if (gen !== sessionGenerationRef.current) {
          clearInitializing();
          return;
        }
        if (shouldFailClosed()) {
          setTokenAcquisitionError(safeError);
        } else {
          // See matching comment in the async .catch() branch above: a transient failure with a
          // still-valid held token needs its own retry scheduled, or nothing will ever try again.
          clearRefreshTimer();
          if (isMountedRef.current) {
            refreshTimerRef.current = setTimeout(() => {
              checkSession();
            }, MIN_REFRESH_DELAY_MS);
          }
        }
      }
      clearInitializing();
    }
  }, [instance, accounts, clearInitializing, clearRefreshTimer, shouldFailClosed]);

  useEffect(() => {
    isMountedRef.current = true;

    if (hasLoggedOutDueToError) {
      setIsSsoAttempting(false);
      return;
    }

    const trySSO = async (): Promise<void> => {
      if (accounts.length === 0) {
        try {
          const ssoResponse = await attemptSsoSilent();
          if (ssoResponse) {
            console.log('SSO Silent authentication successful');
            addDatadogLog('info', 'SSO Silent authentication successful', {
              source: 'AuthContext.trySSO',
            });
          }
        } catch (error) {
          // SSO silent typically fails when the user is not logged into Windows
          // with their corporate account; this is expected, not an error.
          // Forward only the scrubbed name/errorCode — the raw message can carry
          // tenant/account hints and this log lands in Datadog Logs (EU). Don't
          // pass the raw error to console either (console.error/uncaught are
          // forwarded to Datadog Logs via forwardErrorsToLogs).
          const { context } = normalizeErrorForTelemetry(error, {
            genericMessage: 'SSO silent failed',
          });
          console.log('SSO Silent failed');
          addDatadogLog('info', 'SSO Silent failed', {
            source: 'AuthContext.trySSO',
            ...context,
          });
        } finally {
          setIsSsoAttempting(false);
        }
      } else {
        setIsSsoAttempting(false);
      }
    };

    const isAuthRedirectEnabled = import.meta.env.VITE_FEATURE_FLAG_AUTH_REDIRECT === 'true';

    // logoutInProgressRef only gates checkSession() directly — it says nothing to ssoSilent().
    // Without this, the DOM 'logout' path (handleLogout never sets hasLoggedOutDueToError) hits
    // an open gap: MSAL's accounts can empty before logoutRedirect's AAD round trip finishes, and
    // this effect re-runs on that very accounts transition. If the AAD session cookie is still
    // live at that moment, ssoSilent() can succeed and quietly sign the user back in while they
    // are mid-navigation to AAD's own logout endpoint. Skip using the render-time snapshot, not
    // the live ref — the reset effect above already flipped the live ref for this exact
    // transition by the time this effect body runs.
    if (isAuthRedirectEnabled && !wasLogoutInProgressThisRender) {
      trySSO();
    } else {
      setIsSsoAttempting(false);
    }

    checkSession();

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    const handleLogout = (): void => {
      // See the authError effect above: discard any in-flight silent acquisition from here on.
      // Especially load-bearing on this path — platformLogout() is an async browser navigation,
      // so the provider stays mounted and interactive for the whole redirect window, during which
      // MSAL still reports an account and further checkSession() calls can run.
      sessionGenerationRef.current += 1;
      logoutInProgressRef.current = true;
      // Full session teardown (drops the token BEFORE removeQueries so the OSAT eligibility query
      // can't refetch on this user's token and re-arm osat:evaluated — the 1042335 regression),
      // plus refresh-timer/expiry cleanup.
      clearSessionState();
      platformLogout(instance).catch((error: unknown) => {
        // If the redirect itself never fires, MSAL's cache is never cleared, accounts.length
        // never reaches 0, and the effect that resets logoutInProgressRef never runs — leaving
        // checkSession()/ssoSilent() blocked forever. Reset it here so a failed logout degrades
        // to "refresh keeps working" instead of permanently disabling refresh.
        const { error: safeError, context } = normalizeErrorForTelemetry(error, {
          genericMessage: 'Logout redirect failed',
        });
        addDatadogError(safeError, {
          source: 'AuthContext.handleLogout.platformLogout',
          ...context,
        });
        logoutInProgressRef.current = false;
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('logout', handleLogout);

    return (): void => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('logout', handleLogout);
      clearRefreshTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts]);

  useEffect(() => {
    const handleInvalidToken = (): void => {
      checkSession();
    };
    document.addEventListener('invalidaccesstoken', handleInvalidToken);
    return () => document.removeEventListener('invalidaccesstoken', handleInvalidToken);
  }, [checkSession]);

  const values = useMemo(
    () => ({
      userProfile,
      accessToken: effectiveAccessToken,
      accessTokenRef,
      checkSession,
      waitForNextToken,
      isSsoAttempting,
      hasPermission,
      isAuthLoading,
      isFullyAuthenticated,
      isAuthInitializing,
      authError,
      flags: flags ?? null,
    }),
    [
      userProfile,
      accessToken,
      checkSession,
      waitForNextToken,
      isSsoAttempting,
      hasPermission,
      isAuthLoading,
      isFullyAuthenticated,
      isAuthInitializing,
      authError,
      flags,
    ],
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
